import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import axiosClient from "../api/axiosClient";
import { getCourseTheme } from "../script/coursesGetcourses";
import { useLocation } from "react-router-dom";
import "../styles/Dalily.css";

import { Helmet } from "react-helmet";

export default function DailyQuest() {
  const location = useLocation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  // 🔥 NEW STATES
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [questionData, setQuestionData] = useState(null);
  const [loadingQ, setLoadingQ] = useState(false);
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  // const [feedbackDone, setFeedbackDone] = useState(false);

  const [tabSwitches, setTabSwitches] = useState(0);
  const [backspaceCount, setBackspaceCount] = useState(0);
  const [startTime, setStartTime] = useState(Date.now());

  const [forceResume, setForceResume] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // 🔥 UI-only additions: live timer + synced line numbers
  const [liveSeconds, setLiveSeconds] = useState(0);
  const editorRef = useRef(null);
  const lineNumbersRef = useRef(null);

  useEffect(() => {
    const resumeRaw = localStorage.getItem("resumeDailyQuest");

    if (resumeRaw) {
      setIsResuming(true);

      const resume = JSON.parse(resumeRaw);

      if (resume?.course) {
        fetchDailyQuestion(resume.course);

        // 🔥 URL FIX
        navigate(`/daily?course=${encodeURIComponent(resume.course)}`, {
          replace: true,
        });
      }

      // 🔥 CODE RESTORE (👉 YAHI ADD KARNA THA)
      if (resume?.code) {
        setCode(resume.code);
      }

      setTimeout(() => {
        localStorage.removeItem("resumeDailyQuest");
      }, 500);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const course = params.get("course");

    if (course) {
      setSelectedCourse(course); // 🔥 ADD THIS LINE
      fetchDailyQuestion(course);
    }
  }, [location.search]);

  useEffect(() => {
    const handleBlur = () => {
      setTabSwitches((prev) => prev + 1);
    };

    window.addEventListener("blur", handleBlur);

    return () => window.removeEventListener("blur", handleBlur);
  }, []);

  // 🔥 LIVE TIMER — purely visual, ticks while a question is open
  useEffect(() => {
    if (!selectedCourse) return;

    const interval = setInterval(() => {
      setLiveSeconds(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [selectedCourse, startTime]);

  const handleKeyDown = (e) => {
    if (e.key === "Backspace") {
      setBackspaceCount((prev) => prev + 1);
    }
  };

  const handleEditorScroll = () => {
    if (lineNumbersRef.current && editorRef.current) {
      lineNumbersRef.current.scrollTop = editorRef.current.scrollTop;
    }
  };

  // 🔥 LOAD COURSES
  useEffect(() => {
    const loadTree = async () => {
      try {
        const res = await axiosClient.get("/no-auth/api/users/tree");
        const finalData = res.data?.data || res.data;
        setData(Array.isArray(finalData) ? finalData : []);
      } catch (err) {
        Swal.fire("Error", "Failed to load courses", "error");
      } finally {
        setLoading(false);
      }
    };

    loadTree();
  }, []);

  // 🔥 FETCH DAILY QUESTION
  const fetchDailyQuestion = async (courseName) => {
    try {
      setLoadingQ(true);

      // 🔥 CLEAN MODULE NAME
      const moduleName = courseName?.replace(/_/g, " ")?.toUpperCase()?.trim();

      console.log("MODULE:", moduleName);

      // 🔥 URL PARAM (WORKING VERSION)
      const res = await axiosClient.get(
        `/no-auth/api/users/getDaliyChallenge?module=${moduleName}`,
      );

      setSelectedCourse(moduleName);
      setQuestionData(res.data);
    } catch (err) {
      console.log("ERROR:", err);
      Swal.fire("Error", "Failed to load question", "error");
    } finally {
      setLoadingQ(false);
    }
  };

  const userData = JSON.parse(localStorage.getItem("user") || "{}");

  const totalTimeSeconds = Math.floor((Date.now() - startTime) / 1000);

  const resetTracking = () => {
    setTabSwitches(0);
    setBackspaceCount(0);
    setStartTime(Date.now());
    setLiveSeconds(0);
    setCode("");
  };

  const handleSubmit = async () => {
    const token = localStorage.getItem("token");

    // 🔐 LOGIN CHECK
    if (!token) {
      localStorage.setItem("fromDailyQuest", "true");

      localStorage.setItem(
        "resumeDailyQuest",
        JSON.stringify({
          course: selectedCourse,
          code: code   // 🔥 ADD THIS
        })
      );
      Swal.fire({
        title: "Login Required 🔐",
        text: "Please login to submit your answer.",
        icon: "info",
        confirmButtonText: "Login",
      }).then(() => {
        navigate("/login");
      });

      return;
    }

    // ⚠️ EMPTY CODE CHECK
    if (!code || code.trim() === "") {
      Swal.fire({
        title: "Empty Submission ⚠️",
        text: "Please write your solution before submitting.",
        icon: "warning",
      });
      return;
    }

    // 🔥 PREVENT DOUBLE SUBMIT
    if (submitting) return;
    setSubmitting(true);

    // 📦 PAYLOAD
    const payload = {
      userId: userData?.id,
      testName: questionData?.testName || "Daily Challenge",
      questionId:
        questionData?.id ||
        questionData?.questionId ||
        questionData?.question_id,
      questionFormat: questionData?.questionType === "MCQ" ? "MCQ" : "CODING",
      userAnswer: code,
      tabSwitches,
      totalTimeSeconds: Math.floor((Date.now() - startTime) / 1000),
      backspaceCount,
      totalKeystrokes: code.length,
      initialThinkingTimeMs:
        Math.floor((Date.now() - startTime) / 1000) * 1000,
    };

    try {
      const res = await axiosClient.post(
        "/auth/submit-daily-challenge",
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // ✅ SUCCESS POPUP (ONLY IF BACKEND CONFIRMS)
      if (res?.data?.status === 200) {
        Swal.fire({
          title: "Submitted 🎉",
          text: res?.data?.message,
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });

        // 🔥 DELAYED REDIRECT (smooth UX)
        setTimeout(() => {
          resetTracking();
          setSelectedCourse(null);
          setQuestionData(null);
          navigate("/front");
        }, 1500);
      }
    } catch (err) {
      const msg = err?.response?.data?.message || "";

      // 🔴 ALREADY COMPLETED
      if (err?.response?.status === 400) {
        await Swal.fire({
          title: "Already Completed ⚠️",
          text: msg,
          icon: "info",
          confirmButtonText: "Go to Dashboard",
        });

        navigate("/front");
      } else {
        await Swal.fire({
          title: "Error ❌",
          text: msg || "Something went wrong",
          icon: "error",
        });
      }
    } finally {
      // 🔥 ALWAYS RESET
      setSubmitting(false);
    }
  };
  const convertToPoints = (text) => {
    if (!text) return { intro: "", points: [] };

    // split BEFORE 1. 2. 3.
    const parts = text.split(/(?=\d+[\.\)]\s)/);

    return {
      intro: parts[0]?.trim(),
      points: parts.slice(1).map((p) => p.trim()),
    };
  };

  const splitNumberedText = (text) => {
    if (!text) return [];

    return text
      .split(/(?<=\.)\s+(?=\d+\.\s)/)
      // 👆 split only after full stop + space + next number
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
  };

  // 🔥 UI helpers
  const formatClock = (secs) => {
    const m = Math.floor(secs / 60).toString().padStart(2, "0");
    const s = Math.floor(secs % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const difficultyClass = (level) => {
    const l = (level || "").toLowerCase();
    if (l.includes("easy")) return "easy";
    if (l.includes("medium")) return "medium";
    if (l.includes("hard")) return "hard";
    return "default";
  };

  const codeLines = Math.max(code.split("\n").length, 1);

  return (
    <>
      <Helmet>
        <title>Daily Tech Skill Assessments & Challenges | CodeMatrix</title>
        <meta
          name="description"
          content="Boost your coding logic with daily technical challenges. Use CodeMatrix to identify skill gaps, master interview problems, and track your path to top-tier MNCs."
        />
      </Helmet>

      <div className="dq-shell">
        {/* HEADER */}
        {!selectedCourse && (
          <div className="dq-topbar">
            <button className="dq-home dq-home-standalone" onClick={() => navigate("/")}>
              🏠 Home
            </button>
            <span className="dq-eyebrow">🔥 Today's Quest</span>
            <h1 className="dq-page-title">Skill Challenges</h1>
            <p className="dq-page-subtitle">
              Pick a track, solve today's problem, keep your streak alive.
            </p>
          </div>
        )}

        {/* 🔥 QUESTION VIEW */}
        {selectedCourse && (
          <div className="dq-layout">
            {/* STATUS STRIP */}
            <div className="dq-status-strip">
              <div className="dq-strip-left">
                <button
                  className="dq-back"
                  onClick={() => {
                    setSelectedCourse(null);
                    setQuestionData(null);
                    resetTracking();
                    navigate("/daily");
                  }}
                >
                  ⬅ Back to tracks
                </button>

                <button className="dq-home" onClick={() => navigate("/")}>
                  🏠 Home
                </button>
              </div>

              <div className="dq-timer">
                <span className="dq-timer-dot" />
                {formatClock(liveSeconds)} on the clock
              </div>

              <span
                className={`dq-badge ${difficultyClass(
                  questionData?.difficultyLevel
                )}`}
              >
                {questionData?.difficultyLevel || "Loading..."}
              </span>
            </div>

            {/* LEFT SIDE - QUESTION (the "scroll") */}
            <div className="dq-left">
              {loadingQ ? (
                <div className="dq-skeleton" />
              ) : (
                <>
                  <div className="dq-section">
                    <h3>🧠 Problem</h3>
                    {(() => {
                      const { intro, points } = convertToPoints(
                        questionData?.question
                      );

                      return (
                        <>
                          <p style={{ marginBottom: "12px" }}>{intro}</p>

                          <ol>
                            {points.map((p, i) => (
                              <li key={i}>
                                {p.replace(/^\d+[\.\)]\s*/, "")}
                              </li>
                            ))}
                          </ol>
                        </>
                      );
                    })()}
                  </div>

                  <div className="dq-section">
                    <h3>📌 Constraints</h3>
                    <ol>
                      {splitNumberedText(questionData?.constraints).map(
                        (point, i) => (
                          <li key={i}>{point.replace(/^\d+\.\s*/, "")}</li>
                        )
                      )}
                    </ol>
                  </div>

                  {questionData?.examplesJson && (
                    <div className="dq-section">
                      <h3>📊 Examples</h3>

                      {JSON.parse(questionData.examplesJson).map((ex, i) => (
                        <div key={i} className="example-card">
                          <h4 className="example-title">Example {i + 1}</h4>

                          <div className="example-block">
                            <span>Input</span>
                            <pre>{ex.input}</pre>
                          </div>

                          <div className="example-block">
                            <span>Output</span>
                            <pre>{ex.output}</pre>
                          </div>

                          {ex.explanation && (
                            <div className="example-block">
                              <span>Explanation</span>
                              <p>{ex.explanation}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* RIGHT SIDE - the console */}
            <div className="dq-right">
              {submitting && (
                <div className="submit-overlay">
                  <div className="loader-box">
                    <div className="spinner"></div>
                    <p>Submitting your solution...</p>
                  </div>
                </div>
              )}

              <div className="dq-editor-tabs">
                <div className="dq-editor-dots">
                  <span />
                  <span />
                  <span />
                </div>
                <div className="dq-editor-tab">
                  <span className="dq-dot-unsaved" />
                  solution.js
                </div>
              </div>

              <div className="dq-editor-body">
                <div className="dq-line-numbers" ref={lineNumbersRef}>
                  {Array.from({ length: codeLines }, (_, i) => i + 1).join(
                    "\n"
                  )}
                </div>

                <textarea
                  ref={editorRef}
                  className="code-editor"
                  value={code}
                  placeholder="// Write your solution here..."
                  onChange={(e) => setCode(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onScroll={handleEditorScroll}
                  spellCheck={false}
                />
              </div>

              <div className="dq-editor-footer">
                <div className="dq-editor-stats">
                  <span>{codeLines} lines</span>
                  <span>{code.length} chars</span>
                </div>

                <button
                  className="submit-btn"
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting ? "Submitting..." : "🚀 Submit Code"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 🔥 COURSE GRID */}
        {!selectedCourse && !isResuming && !location.search && (
          <div className="dq-grid">
            {loading &&
              Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="dq-skeleton" />
              ))}

            {!loading &&
              data
                .filter((item) => {
                  const name = (item.name || item.course)?.toUpperCase();

                  return (
                    name.includes("DSA") ||
                    name.includes("DATA SCIENCE") ||
                    name.includes("PYTHON") ||
                    name.includes("JAVA") ||
                    name === "C" ||
                    name.includes("C LANGUAGE")
                  );
                })
                .map((item, index) => {
                  const theme = getCourseTheme(item.name || item.course);

                  return (
                    <div
                      key={index}
                      className="dq-card"
                      style={{ "--card-accent": theme.color }}
                      onClick={() => {
                        const name = (item.name || item.course)?.replace(
                          /_/g,
                          " "
                        );

                        resetTracking();

                        navigate(`/daily?course=${encodeURIComponent(name)}`);
                      }}
                    >
                      <div
                        className="dq-card-icon"
                        style={{ background: theme.bg, color: theme.color }}
                      >
                        {theme.icon}
                      </div>

                      <h3 className="dq-card-title">
                        {(item.name || item.course)?.replace(/_/g, " ")}
                      </h3>

                      <div className="dq-card-cta" style={{ color: theme.color }}>
                        Start Practice →
                      </div>
                    </div>
                  );
                })}

            {!loading && data.length === 0 && (
              <div className="dq-empty" style={{ gridColumn: "1 / -1" }}>
                <h3>No tracks yet</h3>
                <p>Check back soon — new quest tracks are on the way.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}